import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import AccountMasterHelp from "../../../../Helper/AccountMasterHelp";

const API_URL = process.env.REACT_APP_API;

const CustomerLimit = () => {
  const company_code = sessionStorage.getItem("Company_Code");
  const user_id = sessionStorage.getItem("User_ID");
  const username = sessionStorage.getItem("username");

  const [selectedAcCode, setSelectedAcCode] = useState("");
  const [selectedAcName, setSelectedAcName] = useState("");
  const [selectedAccoid, setSelectedAccoid] = useState(null);


  const [dailyBuyLimit, setDailyBuyLimit] = useState("");
  const [dailySellLimit, setDailySellLimit] = useState("");


  const [additionalBuyAmount, setAdditionalBuyAmount] = useState("");
  const [additionalSellAmount, setAdditionalSellAmount] = useState("");


  const [entries, setEntries] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [buyBalance, setBuyBalance] = useState(null);
  const [sellBalance, setSellBalance] = useState(null);
  const [posting, setPosting] = useState(false);

  const selectedAccoidRef = useRef(selectedAccoid);
  useEffect(() => {
    selectedAccoidRef.current = selectedAccoid;
  }, [selectedAccoid]);


  useEffect(() => {
    let socket;
    let reconnectTimer;
    let isMounted = true;

    const connect = () => {
      if (!isMounted) return;
      socket = new WebSocket("ws://localhost:8000/ws");
      socket.onopen = () => { };
      socket.onmessage = (event) => {
        const msg = String(event.data).toLowerCase();
        if (msg.includes("refresh_tenders") && selectedAccoidRef.current) {
          fetchAccountEntries(selectedAccoidRef.current);
        }
      };
      socket.onclose = () => {
        if (!isMounted) return;
        reconnectTimer = setTimeout(connect, 3000);
      };
    };

    connect();
    return () => {
      isMounted = false;
      clearTimeout(reconnectTimer);
      if (socket) socket.close();
    };
  }, []);


  useEffect(() => {
    if (selectedAccoid) {
      setBuyBalance(null);
      setSellBalance(null);
      setEntries([]);
      fetchAccountEntries(selectedAccoid);
    } else {
      setEntries([]);
      setBuyBalance(null);
      setSellBalance(null);
    }
  }, [selectedAccoid]);

  const fetchAccountEntries = async (accoid) => {
    setLoadingEntries(true);
    try {
      const res = await axios.get(`${API_URL}/getall-balance-limits`, {
        params: { company_code },
      });
      if (res.data?.success) {
        const accountEntries = res.data.data.filter(
          (e) => String(e.accoid) === String(accoid)
        );
        setEntries(accountEntries);


        const buyBal = accountEntries
          .filter((e) => e.Tran_Type === "BL")
          .reduce((sum, e) => {
            const amt = parseFloat(e.Limit) || 0;
            return e.DRCR === "C" ? sum + amt : sum - amt;
          }, 0);


        const sellBal = accountEntries
          .filter((e) => e.Tran_Type === "SL")
          .reduce((sum, e) => {
            const amt = parseFloat(e.Limit) || 0;
            return e.DRCR === "C" ? sum + amt : sum - amt;
          }, 0);

        setBuyBalance(buyBal);
        setSellBalance(sellBal);
      }
    } catch (err) {
      toast.error("Failed to load ledger.");
    } finally {
      setLoadingEntries(false);
    }
  };



  const handleAccountSelect = (
    code, accoid, name, mobile, gst,
    tds, gstState, city, stateName,
    ebuyLimit, ebuySlLimit
  ) => {

    setEntries([]);
    setBuyBalance(null);
    setSellBalance(null);
    setAdditionalBuyAmount("");
    setAdditionalSellAmount("");


    setDailyBuyLimit("");
    setDailySellLimit("");

    setSelectedAcCode(code);
    setSelectedAcName(name);
    setSelectedAccoid(accoid);

    if (ebuyLimit && parseFloat(ebuyLimit) !== 0) {
      setDailyBuyLimit(String(ebuyLimit));
    }
    if (ebuySlLimit && parseFloat(ebuySlLimit) !== 0) {
      setDailySellLimit(String(ebuySlLimit));
    }
  };


  const validateAmount = (val, fieldName) => {
    if (val < 0) {
      Swal.fire(
        "Negative Value Not Allowed",
        `${fieldName} cannot be negative.`,
        "error"
      );
      return false;
    }
    if (val % 5 !== 0) {
      Swal.fire(
        "Invalid Amount",
        `${fieldName} must be a multiple of 5 (e.g. 5, 10, 15, 20...).`,
        "error"
      );
      return false;
    }
    return true;
  };


  const handleSave = async (section) => {
    if (!selectedAccoid) {
      Swal.fire("Account Missing", "Please select a customer account first.", "info");
      return;
    }

    const payload = {
      Ac_Code: selectedAcCode,
      accoid: selectedAccoid,
      Doc_Date: new Date().toISOString().split("T")[0],
      User_Id: user_id,
      username: username,
    };

    let hasAnything = false;


    if ((section === "buy" || section === "both") && dailyBuyLimit !== "") {
      const val = parseFloat(dailyBuyLimit);
      if (isNaN(val)) {
        Swal.fire("Invalid", "Daily Buy Limit must be a valid number.", "warning");
        return;
      }
      if (!validateAmount(val, "Daily Buy Limit")) return;
      payload.daily_buy_limit = val;
      hasAnything = true;
    }


    if ((section === "sell" || section === "both") && dailySellLimit !== "") {
      const val = parseFloat(dailySellLimit);
      if (isNaN(val)) {
        Swal.fire("Invalid", "Daily Sell Limit must be a valid number.", "warning");
        return;
      }
      if (!validateAmount(val, "Daily Sell Limit")) return;
      payload.daily_sell_limit = val;
      hasAnything = true;
    }


    if ((section === "buy" || section === "both") && additionalBuyAmount !== "") {
      const val = parseFloat(additionalBuyAmount);
      if (isNaN(val) || val <= 0) {
        Swal.fire("Invalid", "Additional Buy amount must be positive.", "warning");
        return;
      }
      if (!validateAmount(val, "Additional Buy Limit")) return;
      payload.additional_buy_amount = val;
      payload.buy_drcr = "C";
      payload.buy_narration = `${username} added Buy limit ${val}`;
      hasAnything = true;
    }


    if ((section === "sell" || section === "both") && additionalSellAmount !== "") {
      const val = parseFloat(additionalSellAmount);
      if (isNaN(val) || val <= 0) {
        Swal.fire("Invalid", "Additional Sell amount must be positive.", "warning");
        return;
      }
      if (!validateAmount(val, "Additional Sell Limit")) return;
      payload.additional_sell_amount = val;
      payload.sell_drcr = "C";
      payload.sell_narration = `${username} added Sell limit ${val}`;
      hasAnything = true;
    }

    if (!hasAnything) {
      Swal.fire("Nothing to Save", "Please enter at least one value.", "info");
      return;
    }

    const confirm = await Swal.fire({
      title: "Confirm Save",
      html: `Save limit changes for <b>${selectedAcName}</b>?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Save",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });
    if (!confirm.isConfirmed) return;

    setPosting(true);
    try {
      const res = await axios.post(
        `${API_URL}/save-customer-limit`,
        payload,
        { params: { company_code } }
      );
      if (res.data?.success) {
        Swal.fire("Saved!", res.data.message, "success");
        setAdditionalBuyAmount("");
        setAdditionalSellAmount("");
        fetchAccountEntries(selectedAccoid);
      }
    } catch (err) {
      Swal.fire("Error", err.response?.data?.error || "Server error.", "error");
    } finally {
      setPosting(false);
    }
  };


  const handleReduce = async (type) => {
    if (!selectedAccoid) {
      Swal.fire("Account Missing", "Please select a customer account first.", "info");
      return;
    }

    const rawAmt = type === "buy" ? additionalBuyAmount : additionalSellAmount;
    const amt = parseFloat(rawAmt);

    if (!amt || amt <= 0) {
      Swal.fire("Invalid Amount", "Please enter a positive numeric value.", "warning");
      return;
    }


    if (!validateAmount(amt, `${type === "buy" ? "Buy" : "Sell"} Reduction Amount`))
      return;


    const currentBalance = type === "buy" ? (buyBalance || 0) : (sellBalance || 0);
    if (amt > currentBalance) {
      Swal.fire({
        title: "Insufficient Balance",
        html: `You are trying to reduce <b>${amt.toFixed(2)} Qntl</b> but the 
               current <b>${type === "buy" ? "Buy" : "Sell"} Balance</b> is only 
               <b>${currentBalance.toFixed(2)} Qntl</b>.<br/><br/>
               Reduction cannot exceed available balance.`,
        icon: "error",
        confirmButtonColor: "#e53e3e",
      });
      return;
    }

    const confirm = await Swal.fire({
      title: `Reduce ${type === "buy" ? "Buy" : "Sell"} Limit?`,
      html: `Reduce <b>${amt.toFixed(2)} Qntl</b> for <b>${selectedAcName}</b><br/>
             <small style="color:#718096">
               Remaining after reduction: <b>${(currentBalance - amt).toFixed(2)} Qntl</b>
             </small>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });
    if (!confirm.isConfirmed) return;

    setPosting(true);
    try {
      const payload = {
        Ac_Code: selectedAcCode,
        accoid: selectedAccoid,
        Doc_Date: new Date().toISOString().split("T")[0],
        User_Id: user_id,
        username: username,
      };

      if (type === "buy") {
        payload.additional_buy_amount = amt;
        payload.buy_drcr = "D";
        payload.buy_narration = `${username} reduced Buy limit ${amt}`;
      } else {
        payload.additional_sell_amount = amt;
        payload.sell_drcr = "D";
        payload.sell_narration = `${username} reduced Sell limit ${amt}`;
      }

      const res = await axios.post(
        `${API_URL}/save-customer-limit`,
        payload,
        { params: { company_code } }
      );
      if (res.data?.success) {
        Swal.fire(
          "Done!",
          `${type === "buy" ? "Buy" : "Sell"} limit reduced successfully.`,
          "success"
        );
        type === "buy"
          ? setAdditionalBuyAmount("")
          : setAdditionalSellAmount("");
        fetchAccountEntries(selectedAccoid);
      }
    } catch (err) {
      Swal.fire("Error", err.response?.data?.error || "Server error.", "error");
    } finally {
      setPosting(false);
    }
  };


  const inputStyle = {
    width: "100%",
    padding: "10px",
    borderRadius: "8px",
    border: "2px solid #cbd5e0",
    fontSize: "15px",
    fontWeight: "600",
    boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block",
    fontSize: "12px",
    fontWeight: "700",
    color: "#4a5568",
    marginBottom: "6px",
    textAlign: "left",
  };

  const sectionCard = {
    background: "#f7fafc",
    borderRadius: "10px",
    padding: "16px",
    marginBottom: "18px",
    border: "1px solid #e2e8f0",
  };

  const btnGreen = {
    flex: 1, padding: "10px", background: "#38a169",
    color: "white", border: "none", borderRadius: "7px",
    fontWeight: "700", cursor: "pointer", fontSize: "13px",
  };

  const btnRed = {
    flex: 1, padding: "10px", background: "#e53e3e",
    color: "white", border: "none", borderRadius: "7px",
    fontWeight: "700", cursor: "pointer", fontSize: "13px",
  };


  return (
    <div style={{ fontFamily: "'Segoe UI', Roboto, sans-serif", marginTop: "-60px" }}>
      <ToastContainer autoClose={2000} />


      <h2 style={{ margin: 0, fontSize: "20px", fontWeight: "600", textAlign: "center", color: "#2d3748", marginBottom: "30px" }}>
        eBuy Sugar Customer Limit
      </h2>


      <div style={{ display: "flex", gap: "10px", margin: "0 auto" }}>

        <div style={{
          flex: "0 0 430px", background: "white",
          borderRadius: "12px", boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          height: "fit-content", padding: "28px",
          position: "sticky", top: "25px",
        }}>


          <div style={{ marginBottom: "20px" }}>
            <label style={labelStyle}>Customer Account</label>
            <AccountMasterHelp
              onAcCodeClick={handleAccountSelect}
              CategoryName={selectedAcName}
              CategoryCode={selectedAcCode}
              name="Customer_Account"
              Ac_type={["P", "M","Z"]}
            />
          </div>


          <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>

            <div style={{
              flex: 1, background: "#ebf8ff", padding: "14px",
              borderRadius: "10px", textAlign: "center", border: "1px solid #bee3f8",
            }}>
              <div style={{ fontSize: "11px", fontWeight: "800", color: "#2b6cb0", textTransform: "uppercase" }}>
                Buy Balance
              </div>
              <div style={{
                fontSize: "22px", fontWeight: "800",
                color: (buyBalance || 0) >= 0 ? "#276749" : "#9b2c2c",
              }}>
                {buyBalance === null ? "0.00" : buyBalance.toFixed(2)}
                <small style={{ fontSize: "11px" }}> Qntl</small>
              </div>
            </div>

            <div style={{
              flex: 1, background: "#fff5f5", padding: "14px",
              borderRadius: "10px", textAlign: "center", border: "1px solid #fed7d7",
            }}>
              <div style={{ fontSize: "11px", fontWeight: "800", color: "#c53030", textTransform: "uppercase" }}>
                Sell Balance
              </div>
              <div style={{
                fontSize: "22px", fontWeight: "800",
                color: (sellBalance || 0) >= 0 ? "#276749" : "#9b2c2c",
              }}>
                {sellBalance === null ? "0.00" : sellBalance.toFixed(2)}
                <small style={{ fontSize: "11px" }}> Qntl</small>
              </div>
            </div>
          </div>


          <div style={sectionCard}>
            <div style={{ fontSize: "13px", fontWeight: "800", color: "#2b6cb0", marginBottom: "12px", textTransform: "uppercase" }}>
              🛒 Buy Limit
            </div>


            <div style={{ marginBottom: "12px" }}>
              <label style={labelStyle}>Daily Buy Limit</label>
              <input
                type="number"
                min="0"
                step="5"
                value={dailyBuyLimit}
                onChange={(e) => setDailyBuyLimit(e.target.value)}
                placeholder="Empty = not set"
                style={inputStyle}
                disabled={!selectedAccoid}
              />
            </div>


            <div style={{ marginBottom: "12px" }}>
              <label style={labelStyle}>Additional Buy Limit</label>
              <input
                type="number"
                min="0"
                step="5"
                value={additionalBuyAmount}
                onChange={(e) => setAdditionalBuyAmount(e.target.value)}
                placeholder="Multiples of 5 only"
                style={inputStyle}
                disabled={!selectedAccoid}
              />
            </div>


            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={() => handleSave("buy")}
                disabled={posting || !selectedAccoid}
                style={btnGreen}
              >
                + ADD BUY
              </button>
              <button
                onClick={() => handleReduce("buy")}
                disabled={posting || !selectedAccoid}
                style={btnRed}
              >
                − REDUCE BUY
              </button>
            </div>
          </div>


          <div style={sectionCard}>
            <div style={{ fontSize: "13px", fontWeight: "800", color: "#c53030", marginBottom: "12px", textTransform: "uppercase" }}>
              📦 Sell Limit
            </div>


            <div style={{ marginBottom: "12px" }}>
              <label style={labelStyle}>Daily Sell Limit </label>
              <input
                type="number"
                min="0"
                step="5"
                value={dailySellLimit}
                onChange={(e) => setDailySellLimit(e.target.value)}
                placeholder="Empty = not set"
                style={inputStyle}
                disabled={!selectedAccoid}
              />
            </div>


            <div style={{ marginBottom: "12px" }}>
              <label style={labelStyle}>Additional Sell Limit</label>
              <input
                type="number"
                min="0"
                step="5"
                value={additionalSellAmount}
                onChange={(e) => setAdditionalSellAmount(e.target.value)}
                placeholder="Multiples of 5 only"
                style={inputStyle}
                disabled={!selectedAccoid}
              />
            </div>


            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={() => handleSave("sell")}
                disabled={posting || !selectedAccoid}
                style={btnGreen}
              >
                + ADD SELL
              </button>
              <button
                onClick={() => handleReduce("sell")}
                disabled={posting || !selectedAccoid}
                style={btnRed}
              >
                − REDUCE SELL
              </button>
            </div>
          </div>


          <button
            onClick={() => handleSave("both")}
            disabled={posting || !selectedAccoid}
            style={{
              width: "100%", padding: "13px",
              background: "#1a365d", color: "white",
              border: "none", borderRadius: "8px",
              fontWeight: "700", cursor: "pointer", fontSize: "14px",
            }}
          >
            💾 SAVE ALL CHANGES
          </button>
        </div>


        <div style={{
          flex: 1, background: "white", borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          overflow: "hidden", display: "flex", flexDirection: "column",
        }}>
          <div style={{
            padding: "20px 25px", background: "#f7fafc",
            borderBottom: "1px solid #edf2f7",
          }}>
            <h3 style={{ margin: 0, fontSize: "18px", color: "#2d3748" }}>
              Account Ledger Balance
            </h3>
          </div>

          <div style={{ flex: 1, overflowY: "auto", maxHeight: "calc(100vh - 180px)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "80px" }}>
              <thead style={{ position: "sticky", top: 0, background: "#edf2f7", zIndex: 1 }}>
                <tr>
                  <th style={{ padding: "14px", textAlign: "left", fontSize: "12px" }}>Date</th>
                  <th style={{ padding: "14px", textAlign: "center", fontSize: "12px" }}>Tran Type</th>
                  <th style={{ padding: "14px", textAlign: "center", fontSize: "12px" }}>DR/CR</th>
                  <th style={{ padding: "14px", textAlign: "center", fontSize: "12px" }}>Amount</th>
                  <th style={{ padding: "14px", textAlign: "left", fontSize: "12px" }}>Narration</th>
                </tr>
              </thead>
              <tbody>
                {loadingEntries ? (
                  <tr>
                    <td colSpan="5" style={{ textAlign: "center", padding: "50px", color: "#718096" }}>
                      Loading records...
                    </td>
                  </tr>
                ) : entries.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ textAlign: "center", padding: "100px", color: "#a0aec0" }}>
                      Select an account to view Balance
                    </td>
                  </tr>
                ) : (
                  entries.map((entry, idx) => {
                    const isCredit = entry.DRCR === "C";
                    const isBL = entry.Tran_Type === "BL";
                    return (
                      <tr
                        key={idx}
                        style={{
                          borderBottom: "1px solid #f7fafc",
                          background: idx % 2 === 0 ? "white" : "#fafafa",
                        }}
                      >
                        <td style={{ padding: "13px", fontSize: "13px" }}>
                          {new Date(entry.Doc_Date).toLocaleDateString()}
                        </td>
                        <td style={{ padding: "13px", textAlign: "center" }}>
                          <span style={{
                            padding: "3px 10px", borderRadius: "12px",
                            fontSize: "11px", fontWeight: "800",
                            background: isBL ? "#ebf8ff" : "#fff5f5",
                            color: isBL ? "#2b6cb0" : "#c53030",
                          }}>
                            {entry.Tran_Type || "BL"}
                          </span>
                        </td>
                        <td style={{ padding: "13px", textAlign: "center" }}>
                          <span style={{
                            padding: "3px 10px", borderRadius: "12px",
                            fontSize: "11px", fontWeight: "800",
                            background: isCredit ? "#c6f6d5" : "#fed7d7",
                            color: isCredit ? "#22543d" : "#822727",
                          }}>
                            {isCredit ? "CREDIT" : "DEBIT"}
                          </span>
                        </td>
                        <td style={{
                          padding: "13px",
                          fontWeight: "700",
                          color: isCredit ? "#38a169" : "#e53e3e",
                        }}>
                          {isCredit ? "+" : "−"}{parseFloat(entry.Limit).toFixed(2)}
                        </td>
                        <td style={{ padding: "13px", fontSize: "13px", color: "#4a5568", textAlign: "left", }}>
                          {entry.Narration}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};

export default CustomerLimit;